import { Label } from '@/components/ui/label';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { cn } from '@/lib/utils';
import {
  useNotificationPreferences,
  useUpdateNotificationPreference,
} from '@/hooks/use-community';
import {
  NOTIFICATION_PREF_LABELS,
} from '@/types/community';
import type { NotificationPrefKey } from '@/types/community';

const PREF_GROUPS = ['Discussions', 'Study Groups', 'Support'] as const;

export function NotificationPreferencesForm() {
  const { data: prefs, isLoading } = useNotificationPreferences();
  const updatePref = useUpdateNotificationPreference();

  if (isLoading) {
    return (
      <div className="flex justify-center p-4">
        <LoadingSpinner className="h-6 w-6" />
      </div>
    );
  }

  if (!prefs) {
    return (
      <p className="text-sm text-muted-foreground py-4">
        Visit the Community page first to set up your preferences.
      </p>
    );
  }

  const prefEntries = Object.entries(NOTIFICATION_PREF_LABELS) as [NotificationPrefKey, typeof NOTIFICATION_PREF_LABELS[NotificationPrefKey]][];

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
          Email Notifications
        </h3>
        <p className="text-xs text-muted-foreground">
          Choose which community events send you an email notification.
        </p>
      </div>

      {PREF_GROUPS.map(group => {
        const groupPrefs = prefEntries.filter(([, meta]) => meta.group === group);
        if (groupPrefs.length === 0) return null;

        return (
          <div key={group}>
            <Label className="text-gray-700 dark:text-gray-300 text-xs uppercase tracking-wide font-semibold mb-3 block">
              {group}
            </Label>
            <div className="space-y-3">
              {groupPrefs.map(([key, meta]) => (
                <div key={key} className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {meta.label}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {meta.description}
                    </p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={prefs[key]}
                    aria-label={`${meta.label} email notifications`}
                    onClick={() => updatePref.mutate({ key, value: !prefs[key] })}
                    className={cn(
                      'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900 min-h-[28px]',
                      prefs[key]
                        ? 'bg-orange-500'
                        : 'bg-gray-200 dark:bg-gray-700',
                    )}
                  >
                    <span
                      className={cn(
                        'pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow ring-0 transition-transform duration-200 ease-in-out',
                        prefs[key] ? 'translate-x-5' : 'translate-x-0',
                      )}
                    />
                  </button>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
